import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useController } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import { FaUser } from 'react-icons/fa';
import { MdEmail } from 'react-icons/md';
import { EmployeeAvatar, resolvePersonName } from './EmployeeBadge';

// ── Custom react-select renderers ─────────────────────────────────────────────

const optionDataShape = PropTypes.shape({
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  email: PropTypes.string,
  avatarUrl: PropTypes.string
});

export const CustomOption = (props) => {
  const { data, isSelected, isFocused } = props;
  const code       = String(data.label || data.value || '');
  const email      = data.email || null;
  const personName = resolvePersonName(data, code);

  return (
    <components.Option {...props}>
      <div
        className={`tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2.5 tw-transition-colors ${
          isSelected ? 'tw-bg-primary-600' : isFocused ? 'tw-bg-slate-50' : 'tw-bg-white'
        }`}
      >
        <EmployeeAvatar name={code} avatarUrl={data.avatarUrl} size={36} fontSize={12} />
        <div className="tw-min-w-0 tw-flex-1">
          <p className={`tw-m-0 tw-text-sm tw-font-semibold tw-truncate ${isSelected ? 'tw-text-white' : 'tw-text-slate-800'}`}>
            {code}
            {personName && (
              <span className={`tw-ml-1.5 tw-font-normal tw-text-xs ${isSelected ? 'tw-text-primary-200' : 'tw-text-slate-500'}`}>
                {personName}
              </span>
            )}
          </p>
          {email && (
            <p className={`tw-m-0 tw-flex tw-items-center tw-gap-1 tw-text-xs tw-truncate tw-mt-0.5 ${isSelected ? 'tw-text-primary-100' : 'tw-text-slate-400'}`}>
              <MdEmail size={12} className="tw-shrink-0" />
              <span className="tw-truncate">{email}</span>
            </p>
          )}
        </div>
      </div>
    </components.Option>
  );
};

CustomOption.propTypes = {
  data: optionDataShape.isRequired,
  isSelected: PropTypes.bool,
  isFocused: PropTypes.bool
};

// SingleValue shows avatar + code + person name + email inline inside the control
const CustomSingleValue = (props) => {
  const { data } = props;
  const code       = String(data.label || data.value || '');
  const email      = data.email || null;
  const personName = resolvePersonName(data, code);

  return (
    <components.SingleValue {...props}>
      <div className="tw-flex tw-items-center tw-gap-2.5">
        <EmployeeAvatar name={code} avatarUrl={data.avatarUrl} size={28} fontSize={10} />
        <div className="tw-flex tw-flex-col tw-min-w-0">
          <span className="tw-text-sm tw-font-semibold tw-text-slate-800 tw-leading-tight tw-truncate">
            {code}
            {personName && (
              <span className="tw-ml-1.5 tw-text-xs tw-font-normal tw-text-slate-500">{personName}</span>
            )}
          </span>
          {email && (
            <span className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-text-slate-400 tw-leading-tight tw-truncate tw-mt-0.5">
              <MdEmail size={11} className="tw-shrink-0" />
              <span className="tw-truncate">{email}</span>
            </span>
          )}
        </div>
      </div>
    </components.SingleValue>
  );
};

CustomSingleValue.propTypes = { data: optionDataShape.isRequired };

// MultiValue chip (isMulti) — small avatar + code so selected people stay
// recognizable at a glance instead of a plain text tag.
// Summary mode: one line that says how many are picked, instead of a chip per
// person. A filter toolbar has a fixed row height, and a growing stack of chips
// pushes every control beside it out of alignment.
export const CountMultiValue = ({ index, getValue }) => {
  if (index > 0) return null;
  const count = getValue().length;
  return (
    <span className="tw-text-sm tw-font-semibold tw-text-slate-700 tw-whitespace-nowrap">
      {count} selected
    </span>
  );
};

CountMultiValue.propTypes = { index: PropTypes.number, getValue: PropTypes.func };

export const CustomMultiValue = (props) => {
  const { data } = props;
  const code = String(data.label || data.value || '');
  return (
    <components.MultiValue {...props}>
      <div className="tw-flex tw-items-center tw-gap-1.5 tw-py-0.5">
        <EmployeeAvatar name={code} avatarUrl={data.avatarUrl} size={18} fontSize={8} />
        <span className="tw-text-xs tw-font-medium tw-text-slate-700">{code}</span>
      </div>
    </components.MultiValue>
  );
};

CustomMultiValue.propTypes = { data: optionDataShape.isRequired };

export const CustomPlaceholder = (props) => (
  <components.Placeholder {...props}>
    <div className="tw-flex tw-items-center tw-gap-2 tw-text-slate-400">
      <FaUser size={13} />
      <span className="tw-text-sm">{props.children}</span>
    </div>
  </components.Placeholder>
);

CustomPlaceholder.propTypes = { children: PropTypes.node };

const CONTROL_HEIGHT = 38;
// `sm` exists for dense filter toolbars, where a 38px control towers over the
// 32px date pills and dropdowns sitting beside it.
const CONTROL_HEIGHTS = { sm: 32, md: CONTROL_HEIGHT };

const selectStyles = (hasError, isMulti = false, height = CONTROL_HEIGHT, summarize = false) => ({
  control: (base, state) => ({
    ...base,
    minHeight: height,
    height:    isMulti && !summarize ? 'auto' : height,
    borderRadius: 8,
    borderColor: hasError ? '#ef4444' : state.isFocused ? '#4f46e5' : '#e2e8f0',
    boxShadow: state.isFocused
      ? hasError ? '0 0 0 3px rgba(239,68,68,0.12)' : '0 0 0 3px rgba(79,70,229,0.12)'
      : 'none',
    '&:hover': { borderColor: hasError ? '#ef4444' : '#4f46e5' },
    transition: 'all 0.15s ease',
    flexWrap: isMulti && !summarize ? 'wrap' : 'nowrap',
  }),
  valueContainer: (base) => ({
    ...base,
    minHeight: height,
    height:    isMulti && !summarize ? 'auto' : height,
    padding:   '0 10px',
    flexWrap:  isMulti && !summarize ? 'wrap' : 'nowrap',
    overflow:  isMulti && !summarize ? 'visible' : 'hidden',
  }),
  input: (base) => ({
    ...base,
    margin:  0,
    padding: 0,
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: isMulti && !summarize ? 'auto' : height,
  }),
  clearIndicator: (base) => ({
    ...base,
    color: '#94a3b8',
    padding: '0 6px',
    cursor: 'pointer',
    '&:hover': { color: '#ef4444' },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 8,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    border: '1px solid #e2e8f0',
    overflow: 'hidden',
    marginTop: 4,
  }),
  menuList:   (base) => ({ ...base, padding: 4, maxHeight: 260 }),
  menuPortal: (base) => ({ ...base, zIndex: 100001 }),
  option: (base) => ({
    ...base,
    padding: 0,
    backgroundColor: 'transparent',
    '&:active': { backgroundColor: 'transparent' },
  }),
});

// ── EmployeeSelect (controlled — no react-hook-form) ──────────────────────────
// The rich avatar/name/email picker as a plain controlled input (value/onChange).
// Use this directly in manually-managed forms; EmployeeSelectField wraps it for
// react-hook-form.
export const EmployeeSelect = ({
  label,
  value,
  onChange,
  loadOptions,
  required = false,
  error,
  placeholder = 'Select…',
  disabled = false,
  defaultOptions = true,
  isMulti = false,
  size = 'md',
  summarizeSelection = false,
}) => {
  const hasError = Boolean(error);
  const summarize = isMulti && summarizeSelection;
  return (
    <div className="tw-flex tw-flex-col tw-gap-1.5">
      {label && (
        <label className="tw-text-sm tw-font-medium tw-text-slate-700">
          {label}
          {required && <span className="tw-text-red-500 tw-ml-1">*</span>}
        </label>
      )}

      <AsyncSelect
        cacheOptions
        defaultOptions={defaultOptions}
        loadOptions={loadOptions}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        isClearable
        isMulti={isMulti}
        closeMenuOnSelect={!isMulti}
        isDisabled={disabled}
        styles={selectStyles(hasError, isMulti, CONTROL_HEIGHTS[size] || CONTROL_HEIGHT, summarize)}
        hideSelectedOptions={summarize ? false : undefined}
        components={{
          Option:      CustomOption,
          Placeholder: CustomPlaceholder,
          ...(isMulti
            ? { MultiValue: summarize ? CountMultiValue : CustomMultiValue }
            : { SingleValue: CustomSingleValue }),
        }}
        menuPosition="fixed"
        menuPortalTarget={document.body}
        classNamePrefix="emp-select"
      />

      {hasError && (
        <p className="tw-text-xs tw-text-red-500 tw-mt-0.5 tw-mb-0">{error}</p>
      )}
    </div>
  );
};

EmployeeSelect.propTypes = {
  label:          PropTypes.string,
  value:          PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange:       PropTypes.func.isRequired,
  loadOptions:    PropTypes.func.isRequired,
  required:       PropTypes.bool,
  error:          PropTypes.string,
  placeholder:    PropTypes.string,
  disabled:       PropTypes.bool,
  defaultOptions: PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  isMulti:        PropTypes.bool,
  size:           PropTypes.oneOf(['sm', 'md']),
  /** isMulti only: show "N selected" on one line instead of a chip per option. */
  summarizeSelection: PropTypes.bool,
};

// ── EmployeeSelectField (react-hook-form wrapper) ─────────────────────────────

const EmployeeSelectField = ({
  label,
  name,
  control,
  loadOptions,
  required = false,
  error,
  placeholder = 'Select…',
  valueKey = 'value',
  disabled = false,
  defaultValue = null,
  defaultOptions = true,
  isMulti = false,
  requiredMessage,
}) => {
  // `requiredMessage` lets a form that renders its own label (so `label` is
  // omitted here to avoid a duplicate) still get a meaningful validation error
  // instead of the generic "This field is required."
  const { field, fieldState } = useController({
    control,
    name,
    rules: required
      ? { required: requiredMessage || `${label || 'This field'} is required.` }
      : undefined,
  });

  const [selectedOption, setSelectedOption] = useState(defaultValue);

  // An edit form fetches its record asynchronously, so defaultValue usually
  // arrives AFTER the first render — useState alone would leave the field empty
  // forever. Keyed on the serialized content rather than object identity: parents
  // rebuild these options with useMemo/inline maps on every render, and reacting
  // to identity would wipe out whatever the user had just picked.
  const defaultKey = useMemo(() => JSON.stringify(defaultValue ?? null), [defaultValue]);

  useEffect(() => {
    if (defaultValue === null || defaultValue === undefined) return;
    setSelectedOption(defaultValue);
  }, [defaultKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleChange = (option) => {
    setSelectedOption(option);
    if (isMulti) {
      field.onChange(Array.isArray(option) ? option.map((o) => o[valueKey]) : []);
    } else {
      field.onChange(option ? option[valueKey] : null);
    }
  };

  return (
    <EmployeeSelect
      label={label}
      required={required}
      error={error || fieldState.error?.message}
      placeholder={placeholder}
      loadOptions={loadOptions}
      disabled={disabled}
      defaultOptions={defaultOptions}
      isMulti={isMulti}
      value={selectedOption}
      onChange={handleChange}
    />
  );
};

EmployeeSelectField.propTypes = {
  label:        PropTypes.string,
  name:         PropTypes.string.isRequired,
  control:      PropTypes.object.isRequired,
  loadOptions:  PropTypes.func.isRequired,
  required:     PropTypes.bool,
  error:        PropTypes.string,
  placeholder:  PropTypes.string,
  valueKey:     PropTypes.oneOf(['value', 'label']),
  disabled:     PropTypes.bool,
  defaultValue:    PropTypes.oneOfType([
    PropTypes.shape({ value: PropTypes.any, label: PropTypes.string }),
    PropTypes.array,
  ]),
  defaultOptions:  PropTypes.oneOfType([PropTypes.bool, PropTypes.array]),
  isMulti:         PropTypes.bool,
  requiredMessage: PropTypes.string,
};

export default EmployeeSelectField;
